/*
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   useViewDetailPageSection hook
 */

import { useViewSection } from '@zcode-ui/hook'
import { toTraktorEventName } from '@zcode-ui/web-zumper-integrations'
import type { DETAIL } from '@zcode/mixpanel'
import { useCallback } from 'react'
import invariant from 'tiny-invariant'
import { mixpanelViewSection } from '../../../modules/mixpanel/thunks/mixpanelViewSection'
import { trackDetailEvent } from '../../../modules/traktor/thunks/trackDetailEvent'
import { useDispatch } from '../../../modules/useDispatch'

export const useViewDetailPageSection = (
  sectionName:
    | undefined
    | (typeof DETAIL)[keyof typeof DETAIL]
    | [
        mixpanelSectionName: (typeof DETAIL)[keyof typeof DETAIL],
        traktorEventName: `${string}_IMPRESSION`,
      ]
) => {
  const dispatch = useDispatch()
  const [mixpanelSectionName, traktorEventName] = Array.isArray(sectionName)
    ? sectionName
    : [sectionName, sectionName && `DETAIL_${sectionName}_IMPRESSION`]
  const trackViewSection = useCallback(() => {
    invariant(mixpanelSectionName)
    invariant(traktorEventName)
    dispatch(mixpanelViewSection(mixpanelSectionName))
    dispatch(
      trackDetailEvent({
        name: toTraktorEventName(traktorEventName),
      })
    )
  }, [dispatch, mixpanelSectionName, traktorEventName])
  const ref = useViewSection(trackViewSection)
  return mixpanelSectionName ? ref : undefined
}
